import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'react-hot-toast';
import { Save, Plus, Loader2, ArrowLeft, Package, DollarSign, Calculator, AlertTriangle, CheckCircle2, XCircle, Shield, Warehouse, FileText } from 'lucide-react';
import axios from 'axios';
import DynamicFieldsSection from '../components/custom-fields/DynamicFieldsSection';
import HeaderTab from './sales-order-create/HeaderTab';
import ItemsTab from './sales-order-create/ItemsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SalesOrderCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Master Data
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [pricingProcedures, setPricingProcedures] = useState([]);
  
  // Credit Check State
  const [creditCheckResult, setCreditCheckResult] = useState(null);
  const [isCheckingCredit, setIsCheckingCredit] = useState(false);
  
  // ATP Check State
  const [atpResults, setAtpResults] = useState({});
  const [isCheckingATP, setIsCheckingATP] = useState(false);
  
  // Quotation Reference State
  const [eligibleQuotations, setEligibleQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState('');
  const [isLoadingQuotation, setIsLoadingQuotation] = useState(false);
  const [quotationLoaded, setQuotationLoaded] = useState(false);
  
  // Pricing Procedure Auto-fetch State
  const [pricingProcedureSource, setPricingProcedureSource] = useState('');
  
  // Customer Sales Area State - for cascading dropdowns
  const [customerSalesAreas, setCustomerSalesAreas] = useState([]);
  const [availableSalesOrgs, setAvailableSalesOrgs] = useState([]);
  const [availableDistChannels, setAvailableDistChannels] = useState([]);
  const [availableDivisions, setAvailableDivisions] = useState([]);
  
  // Partner Functions State
  const [partnerFunctions, setPartnerFunctions] = useState({
    sold_to: null,  // Current sold-to customer info
    ship_to_parties: [],  // Available ship-to partners
    bill_to_parties: [],  // Available bill-to partners
    payers: []  // Available payers
  });
  
  // New Master Data for Sales Order Header
  const [orderReasons, setOrderReasons] = useState([]);
  const [shippingConditions, setShippingConditions] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  
  // Delivery Block options (hardcoded as SAP standard)
  const deliveryBlockOptions = [
    { code: '', description: 'No Block' },
    { code: '01', description: 'General Delivery Block' },
    { code: '02', description: 'Credit Block' },
    { code: '03', description: 'Quality Block' },
    { code: '04', description: 'Customer Request' },
    { code: '05', description: 'Incomplete Data' }
  ];

  // Form Data
  const [header, setHeader] = useState({
    document_type: 'OR',
    sold_to_party: '',
    ship_to_party: '',
    bill_to_party: '',
    payer: '',
    sales_organization: '',
    distribution_channel: '',
    division: '',
    document_date: new Date().toISOString().split('T')[0],
    requested_delivery_date: '',
    pricing_procedure: '',
    customer_reference: '',
    payment_terms: '',
    incoterms: '',
    incoterms_location: '',
    header_note: '',
    currency: 'USD',
    reference_quotation: '',
    order_reason: '',
    shipping_conditions: '',
    delivery_block: ''
  });

  const [items, setItems] = useState([createNewItem(10)]);

  // Frappe-style custom fields (admin-defined per DocType)
  const [customData, setCustomData] = useState({});

  function createNewItem(itemNumber) {
    return {
      item_number: itemNumber,
      material_id: '',
      item_description: '',
      item_category: 'TAN',
      order_quantity: 1,
      unit_of_measure: 'EA',
      net_price: 0,
      plant: '',
      storage_location: '',
      requested_delivery_date: ''
    };
  }

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setIsLoading(true);
    try {
      const [custRes, orgRes, dcRes, matRes, plantRes, procRes, quotRes, orderReasonRes, shippingCondRes, storageLocRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/pricing/pricing-procedures`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/eligible-quotations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/presales/order-reasons`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-shipping-conditions`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/storage-locations`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
      setDistChannels(dcRes.data || []);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setPricingProcedures(procRes.data || []);
      setEligibleQuotations(quotRes.data || []);
      setOrderReasons(orderReasonRes.data || []);
      setShippingConditions(shippingCondRes.data || []);
      setStorageLocations(storageLocRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerChange = async (customerId) => {
    // Reset sales area selections when customer changes
    setHeader({
      ...header,
      sold_to_party: customerId,
      ship_to_party: customerId,
      bill_to_party: customerId,
      payer: customerId,
      sales_organization: '',
      distribution_channel: '',
      division: '',
      pricing_procedure: '',
      payment_terms: '',
      incoterms: '',
      incoterms_location: ''
    });
    setCreditCheckResult(null);
    setPricingProcedureSource('');
    setCustomerSalesAreas([]);
    setAvailableSalesOrgs([]);
    setAvailableDistChannels([]);
    setAvailableDivisions([]);
    setPartnerFunctions({
      sold_to: null,
      ship_to_parties: [],
      bill_to_parties: [],
      payers: []
    });
    
    if (!customerId) return;
    
    // Fetch customer's sales area data and partner functions
    try {
      const customer = customers.find(c => c.customer_id === customerId);
      if (customer && customer.sales_area_data && customer.sales_area_data.length > 0) {
        const salesAreas = customer.sales_area_data;
        setCustomerSalesAreas(salesAreas);
        
        // Extract unique sales organizations from customer's sales areas
        const uniqueSalesOrgs = [...new Set(salesAreas.map(sa => sa.sales_org_id))].filter(Boolean);
        const filteredSalesOrgs = salesOrgs.filter(org => uniqueSalesOrgs.includes(org.sales_org_id));
        setAvailableSalesOrgs(filteredSalesOrgs);
        
        // If only one sales org, auto-select it
        if (filteredSalesOrgs.length === 1) {
          handleSalesOrgChange(filteredSalesOrgs[0].sales_org_id, salesAreas);
        }
      } else {
        // Customer has no sales area data - show message
        toast.error('Customer has no Sales Area data. Please maintain Sales Area in Customer Master.');
      }
      
      // Load partner functions
      if (customer) {
        const partnerData = customer.partner_function_data || [];
        
        // Build partner lists
        const shipToPartners = [];
        const billToPartners = [];
        const payerPartners = [];
        
        // Always add the sold-to party as default option for all partner roles
        const soldToOption = {
          customer_id: customerId,
          name: customer.name,
          city: customer.city,
          is_default: true
        };
        shipToPartners.push(soldToOption);
        billToPartners.push(soldToOption);
        payerPartners.push(soldToOption);
        
        // Add other partners from partner_function_data
        for (const pf of partnerData) {
          if (pf.partner_number && pf.partner_number !== customerId) {
            // Find the partner customer
            const partnerCustomer = customers.find(c => c.customer_id === pf.partner_number);
            if (partnerCustomer) {
              const partnerOption = {
                customer_id: partnerCustomer.customer_id,
                name: partnerCustomer.name,
                city: partnerCustomer.city,
                is_default: false
              };
              
              if (pf.partner_function_id === 'SH') {
                shipToPartners.push(partnerOption);
              } else if (pf.partner_function_id === 'BP') {
                billToPartners.push(partnerOption);
              } else if (pf.partner_function_id === 'PY') {
                payerPartners.push(partnerOption);
              }
            }
          }
        }
        
        // Also check all customers that have this sold-to as their partner
        for (const c of customers) {
          if (c.customer_id !== customerId && c.partner_function_data) {
            for (const pf of c.partner_function_data) {
              if (pf.partner_number === customerId) {
                // This customer can be a partner for the sold-to
                const partnerOption = {
                  customer_id: c.customer_id,
                  name: c.name,
                  city: c.city,
                  is_default: false
                };
                
                // Add as potential ship-to (any customer can potentially be a ship-to)
                if (!shipToPartners.find(p => p.customer_id === c.customer_id)) {
                  shipToPartners.push(partnerOption);
                }
              }
            }
          }
        }
        
        setPartnerFunctions({
          sold_to: soldToOption,
          ship_to_parties: shipToPartners,
          bill_to_parties: billToPartners,
          payers: payerPartners
        });
      }
    } catch (error) {
      console.error('Error processing customer sales areas:', error);
    }
  };
  
  // Fetch customer pricing procedure from customer master
  const fetchCustomerPricingProcedure = async (customerId, salesOrg, distChannel) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/otc/customer/${customerId}/pricing-procedure`,
        { params: { sales_org: salesOrg, dist_channel: distChannel } }
      );
      
      const data = response.data;
      
      // Update header with fetched data
      const updates = {};
      
      if (data.pricing_procedure) {
        updates.pricing_procedure = data.pricing_procedure;
        setPricingProcedureSource('customer_master');
      } else {
        // Use default pricing procedure if not in customer master
        updates.pricing_procedure = pricingProcedures.length > 0 ? pricingProcedures[0].pricing_procedure_id : 'ZSTD01';
        setPricingProcedureSource('default');
      }
      
      // Also fetch incoterms and payment terms if available
      if (data.incoterms) {
        updates.incoterms = data.incoterms;
      }
      if (data.incoterms_location) {
        updates.incoterms_location = data.incoterms_location;
      }
      if (data.payment_terms) {
        updates.payment_terms = data.payment_terms;
      }
      
      setHeader(prev => ({ ...prev, ...updates }));
      
    } catch (error) {
      console.error('Error fetching customer pricing procedure:', error);
      // Set default pricing procedure
      setHeader(prev => ({ 
        ...prev, 
        pricing_procedure: pricingProcedures.length > 0 ? pricingProcedures[0].pricing_procedure_id : 'ZSTD01' 
      }));
      setPricingProcedureSource('default');
    }
  };
  
  // Handle sales organization change - filter distribution channels
  const handleSalesOrgChange = (salesOrg, salesAreas = customerSalesAreas) => {
    setHeader(prev => ({ 
      ...prev, 
      sales_organization: salesOrg,
      distribution_channel: '',
      division: ''
    }));
    setAvailableDistChannels([]);
    setAvailableDivisions([]);
    
    if (!salesOrg) return;
    
    // Filter distribution channels for selected sales org
    const matchingSalesAreas = salesAreas.filter(sa => sa.sales_org_id === salesOrg);
    const uniqueDistChannels = [...new Set(matchingSalesAreas.map(sa => sa.dist_channel_id))].filter(Boolean);
    const filteredDistChannels = distChannels.filter(dc => uniqueDistChannels.includes(dc.dist_channel_id));
    setAvailableDistChannels(filteredDistChannels);
    
    // If only one distribution channel, auto-select it
    if (filteredDistChannels.length === 1) {
      handleDistChannelChange(filteredDistChannels[0].dist_channel_id, salesOrg, salesAreas);
    }
  };
  
  // Handle distribution channel change - filter divisions and fetch pricing
  const handleDistChannelChange = (distChannel, salesOrg = header.sales_organization, salesAreas = customerSalesAreas) => {
    setHeader(prev => ({ ...prev, distribution_channel: distChannel, division: '' }));
    setAvailableDivisions([]);
    
    if (!distChannel || !salesOrg) return;
    
    // Filter divisions for selected sales org + dist channel
    const matchingSalesAreas = salesAreas.filter(
      sa => sa.sales_org_id === salesOrg && sa.dist_channel_id === distChannel
    );
    const uniqueDivisions = [...new Set(matchingSalesAreas.map(sa => sa.division_id))].filter(Boolean);
    
    // Create division options (we may not have divisions master data, so create from sales areas)
    const divisionOptions = uniqueDivisions.map(div => ({ division_id: div, division_name: div }));
    setAvailableDivisions(divisionOptions);
    
    // If only one division, auto-select it
    if (divisionOptions.length === 1) {
      setHeader(prev => ({ ...prev, division: divisionOptions[0].division_id }));
    }
    
    // Fetch pricing procedure and commercial terms
    if (header.sold_to_party) {
      fetchCustomerPricingProcedure(header.sold_to_party, salesOrg, distChannel);
    }
  };
  
  // Handle quotation selection from dropdown
  const handleQuotationSelect = async (quotationNumber) => {
    if (!quotationNumber || quotationNumber === '__NONE__') {
      handleClearQuotationReference();
      return;
    }
    
    setSelectedQuotation(quotationNumber);
    setIsLoadingQuotation(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/quotation-reference/${quotationNumber}`);
      const data = response.data;
      
      if (data.error) {
        toast.error(data.message);
        setSelectedQuotation('');
        return;
      }
      
      // Populate header data from quotation
      setHeader({
        ...header,
        sold_to_party: data.customer_id,
        ship_to_party: data.ship_to_party || data.customer_id,
        bill_to_party: data.customer_id,
        sales_organization: data.sales_organization,
        distribution_channel: data.distribution_channel,
        division: data.division || '',
        pricing_procedure: data.pricing_procedure || header.pricing_procedure,
        payment_terms: data.payment_terms || '',
        incoterms: data.incoterms || '',
        incoterms_location: data.incoterms_location || '',
        customer_reference: data.customer_reference || '',
        currency: data.currency || 'USD',
        reference_quotation: quotationNumber,
        requested_delivery_date: data.requested_delivery_date || ''
      });
      
      // Also update customer sales areas for cascading dropdowns
      const customer = customers.find(c => c.customer_id === data.customer_id);
      if (customer && customer.sales_area_data) {
        setCustomerSalesAreas(customer.sales_area_data);
        const uniqueSalesOrgs = [...new Set(customer.sales_area_data.map(sa => sa.sales_org_id))].filter(Boolean);
        setAvailableSalesOrgs(salesOrgs.filter(org => uniqueSalesOrgs.includes(org.sales_org_id)));
        
        const matchingSalesAreas = customer.sales_area_data.filter(sa => sa.sales_org_id === data.sales_organization);
        const uniqueDistChannels = [...new Set(matchingSalesAreas.map(sa => sa.dist_channel_id))].filter(Boolean);
        setAvailableDistChannels(distChannels.filter(dc => uniqueDistChannels.includes(dc.dist_channel_id)));
      }
      
      // Populate items from quotation
      if (data.items && data.items.length > 0) {
        setItems(data.items.map((item, idx) => ({
          item_number: item.item_number || (idx + 1) * 10,
          material_id: item.material_id || '',
          item_description: item.item_description || '',
          item_category: item.item_category || 'TAN',
          order_quantity: item.order_quantity || 1,
          unit_of_measure: item.unit_of_measure || 'EA',
          net_price: item.net_price || 0,
          plant: item.plant || '',
          storage_location: item.storage_location || '',
          requested_delivery_date: ''
        })));
      }
      
      setQuotationLoaded(true);
      setPricingProcedureSource('quotation');
      toast.success(data.message);
      
    } catch (error) {
      console.error('Error fetching quotation:', error);
      toast.error(error.response?.data?.detail || 'Failed to load quotation');
      setSelectedQuotation('');
    } finally {
      setIsLoadingQuotation(false);
    }
  };
  
  // Clear quotation reference and reset form
  const handleClearQuotationReference = () => {
    setSelectedQuotation('');
    setQuotationLoaded(false);
    setHeader({
      document_type: 'OR',
      sold_to_party: '',
      ship_to_party: '',
      bill_to_party: '',
      sales_organization: '',
      distribution_channel: '',
      division: '',
      document_date: new Date().toISOString().split('T')[0],
      requested_delivery_date: '',
      pricing_procedure: '',
      customer_reference: '',
      payment_terms: '',
      incoterms: '',
      incoterms_location: '',
      header_note: '',
      currency: 'USD',
      reference_quotation: ''
    });
    setItems([createNewItem(10)]);
    setPricingProcedureSource('');
  };
  
  // Credit Check
  const handleCreditCheck = async () => {
    if (!header.sold_to_party) {
      toast.error('Select a customer first');
      return;
    }
    
    // Calculate total order value
    const totalValue = items.reduce((sum, item) => {
      return sum + (parseFloat(item.order_quantity) || 0) * (parseFloat(item.net_price) || 0);
    }, 0);
    
    setIsCheckingCredit(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/credit/check`, {
        customer_id: header.sold_to_party,
        order_value: totalValue || 100, // Default 100 if no value yet
        currency: header.currency
      });
      setCreditCheckResult(response.data);
      
      if (response.data.status === 'BLOCKED') {
        toast.error(response.data.message);
      } else if (response.data.status === 'WARNING') {
        toast('Warning: ' + response.data.message, { icon: '⚠️' });
      } else {
        toast.success('Credit check passed');
      }
    } catch (error) {
      console.error('Error checking credit:', error);
      toast.error('Credit check failed');
    } finally {
      setIsCheckingCredit(false);
    }
  };
  
  // ATP Check for a single item
  const handleATPCheck = async (itemIndex) => {
    const item = items[itemIndex];
    if (!item.material_id) {
      toast.error('Select a material first');
      return;
    }
    
    // Get requested delivery date (from item or header)
    const requestedDate = item.requested_delivery_date || header.requested_delivery_date || null;
    
    setIsCheckingATP(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/atp/check`, {
        material_id: item.material_id,
        plant: item.plant || null,
        requested_quantity: parseInt(item.order_quantity) || 1,
        unit_of_measure: item.unit_of_measure,
        requested_date: requestedDate
      });
      
      setAtpResults(prev => ({
        ...prev,
        [itemIndex]: response.data
      }));
      
      const result = response.data;
      if (!result.can_fulfill) {
        if (result.confirmed_date) {
          toast.error(`Material ${item.material_id}: Not available. Confirmed delivery: ${result.confirmed_date}`);
        } else {
          toast.error(`Material ${item.material_id}: ${result.message}`);
        }
      } else if (!result.can_meet_requested_date) {
        toast(`Material ${item.material_id}: Available but delivery delayed to ${result.confirmed_date}`, { icon: '⚠️' });
      } else {
        toast.success(`Material ${item.material_id}: Available for ${result.confirmed_date}`);
      }
    } catch (error) {
      console.error('Error checking ATP:', error);
      toast.error('ATP check failed');
    } finally {
      setIsCheckingATP(false);
    }
  };
  
  // ATP Check for all items
  const handleAllATPCheck = async () => {
    const itemsWithMaterial = items.filter(i => i.material_id);
    if (itemsWithMaterial.length === 0) {
      toast.error('No materials to check');
      return;
    }
    
    setIsCheckingATP(true);
    try {
      const requests = itemsWithMaterial.map(item => ({
        material_id: item.material_id,
        plant: item.plant || null,
        requested_quantity: parseInt(item.order_quantity) || 1,
        unit_of_measure: item.unit_of_measure,
        requested_date: item.requested_delivery_date || header.requested_delivery_date || null
      }));
      
      const response = await axios.post(`${BACKEND_URL}/api/otc/atp/check-batch`, requests);
      
      const newResults = {};
      response.data.forEach((result, idx) => {
        const originalIndex = items.findIndex(i => i.material_id === result.material_id);
        if (originalIndex >= 0) {
          newResults[originalIndex] = result;
        }
      });
      setAtpResults(newResults);
      
      const unavailable = response.data.filter(r => !r.can_fulfill);
      const delayed = response.data.filter(r => r.can_fulfill && !r.can_meet_requested_date);
      
      if (unavailable.length > 0) {
        toast.error(`${unavailable.length} item(s) have insufficient stock - check confirmed dates`);
      } else if (delayed.length > 0) {
        toast(`${delayed.length} item(s) available but with delayed delivery`, { icon: '⚠️' });
      } else {
        toast.success('All items available for requested date');
      }
    } catch (error) {
      console.error('Error batch checking ATP:', error);
      toast.error('ATP batch check failed');
    } finally {
      setIsCheckingATP(false);
    }
  };

  const handleAddItem = () => {
    const lastItem = items[items.length - 1];
    const newItemNumber = lastItem ? lastItem.item_number + 10 : 10;
    const newItem = createNewItem(newItemNumber);
    setItems([...items, newItem]);
    setActiveItemIndex(items.length);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.error('Sales order must have at least one item');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(newItems.length - 1);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-fill description from material
    if (field === 'material_id' && value) {
      const material = materials.find(m => m.material_number === value);
      if (material && !newItems[index].item_description) {
        newItems[index].item_description = material.description;
        newItems[index].unit_of_measure = material.base_unit_of_measure || 'EA';
      }
    }
    
    setItems(newItems);
  };

  const validateForm = () => {
    // Header validation
    if (!header.sold_to_party) {
      toast.error('Customer (Sold-to Party) is required');
      setActiveTab('header');
      return false;
    }
    if (!header.sales_organization) {
      toast.error('Sales Organization is required');
      setActiveTab('header');
      return false;
    }
    if (!header.distribution_channel) {
      toast.error('Distribution Channel is required');
      setActiveTab('header');
      return false;
    }
    if (!header.document_date) {
      toast.error('Document Date is required');
      setActiveTab('header');
      return false;
    }

    // Items validation
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.item_description) {
        toast.error(`Item ${item.item_number}: Description is required`);
        setActiveTab('items');
        setActiveItemIndex(i);
        return false;
      }
      if (!item.order_quantity || item.order_quantity <= 0) {
        toast.error(`Item ${item.item_number}: Quantity must be greater than 0`);
        setActiveTab('items');
        setActiveItemIndex(i);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    // Check credit if not already checked or if blocked
    if (creditCheckResult?.status === 'BLOCKED' && !creditCheckResult?.can_proceed) {
      toast.error('Cannot create order - customer credit is blocked');
      return;
    }

    setIsSaving(true);
    try {
      const orderData = {
        ...header,
        pricing_date: header.document_date,
        items: items.map(item => ({
          ...item,
          order_quantity: parseInt(item.order_quantity),
          net_price: parseFloat(item.net_price) || 0,
          requested_delivery_date: item.requested_delivery_date || header.requested_delivery_date || null
        })),
        custom_data: Object.keys(customData).length > 0 ? customData : null,
      };

      const response = await axios.post(`${BACKEND_URL}/api/otc/sales-orders`, orderData);
      toast.success(`Sales Order '${response.data.sales_order_number}' created successfully!`);
      navigate(`/otc/sales-orders/${response.data.sales_order_number}`);
    } catch (error) {
      console.error('Error creating sales order:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create sales order: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const currentItem = items[activeItemIndex] || items[0];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // ctx — single prop bag threaded into HeaderTab + ItemsTab. Parent keeps
  // full state ownership.
  const ctx = {
    // Header tab
    header, setHeader,
    customers, salesOrgs, distChannels,
    availableSalesOrgs, availableDistChannels, availableDivisions,
    partnerFunctions, orderReasons, shippingConditions,
    eligibleQuotations, selectedQuotation, isLoadingQuotation,
    quotationLoaded, pricingProcedureSource,
    creditCheckResult, isCheckingCredit,
    handleSalesOrgChange, handleDistChannelChange,
    handleCreditCheck, handleCustomerChange,
    handleQuotationSelect, handleClearQuotationReference,
    // Items tab
    items, materials, plants, storageLocations,
    atpResults, isCheckingATP,
    activeItemIndex, setActiveItemIndex,
    handleItemChange, handleATPCheck,
    handleAddItem, handleRemoveItem, handleAllATPCheck,
    // Derived
    currentItem,
    // Constants
    deliveryBlockOptions,
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/sales-orders')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Sales Order</h1>
              <p className="text-sm text-gray-600">Create a new customer order with automatic pricing</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-sales-order-btn">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Sales Order
              </>
            )}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(tab) => {
          // Validate header before switching to Items tab
          if (tab === 'items' && !quotationLoaded) {
            if (!header.sold_to_party) {
              toast.error('Please select a Customer first');
              return;
            }
            if (!header.sales_organization) {
              toast.error('Please select a Sales Organization first');
              return;
            }
            if (!header.distribution_channel) {
              toast.error('Please select a Distribution Channel first');
              return;
            }
          }
          setActiveTab(tab);
        }}>
          <TabsList className="mb-4">
            <TabsTrigger value="header">Header Data</TabsTrigger>
            <TabsTrigger 
              value="items"
              className={!header.sold_to_party || !header.sales_organization || !header.distribution_channel ? 'opacity-50' : ''}
            >
              Items ({items.length})
              {(!header.sold_to_party || !header.sales_organization || !header.distribution_channel) && !quotationLoaded && (
                <span className="ml-1 text-xs text-amber-600">*</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="custom_fields" data-testid="so-custom-fields-tab">
              Custom Fields
            </TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <HeaderTab ctx={ctx} />

          {/* Items Tab */}
          <ItemsTab ctx={ctx} />

          {/* Custom Fields Tab — Frappe-style low-code */}
          <TabsContent value="custom_fields">
            <DynamicFieldsSection
              doctype="sales_order"
              values={customData}
              onChange={setCustomData}
              title="Sales Order Custom Fields"
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SalesOrderCreate;
